import React, { useState } from "react";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function MembreLogin() {
  const { t } = useTranslation();
  const [memberId, setMemberId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const navigate = useNavigate();

  async function handleLogin(e) {
    e.preventDefault();

    setLoading(true);
    setIsSuspended(false);

    const cleanMemberId = memberId.trim();

    if (!cleanMemberId || !password) {
      toast.error(t("login.errors.required"));
      setLoading(false);
      return;
    }

    try {
      const res = await fetch("/api/membre?action=login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          member_id: cleanMemberId,
          password,
        }),
      });

      const data = await res.json();

      if (!data.success) {
        if (data.code === "suspended") {
          setIsSuspended(true);
          return;
        }

        if (data.code === "too_many_attempts") {
          toast.error(t("login.errors.tooManyAttempts"));
          return;
        }

        if (data.code === "required") {
          toast.error(t("login.errors.required"));
          return;
        }

        if (data.code === "invalid_credentials") {
          toast.error(t("login.errors.invalidCredentials"));
          return;
        }

        toast.error(t("login.errors.generic"));

        return;
      }

      // Stocker uniquement le JWT nécessaire
      localStorage.setItem("membreToken", data.token);

      // Données minimales pour le frontend
      localStorage.setItem(
        "membre",
        JSON.stringify({
          member_id: data.user.member_id,
          prenom: data.user.prenom,
          avatar:
            data.user.avatar && data.user.avatar.length > 5
              ? data.user.avatar
              : "/images/default-avatar.png",
        }),
      );

      toast.success(t("login.success"));

      // Première connexion
      if (data.user.must_change_password) {
        navigate("/membre/change-first");
        return;
      }

      // Admin
      if (data.user.role === "admin") {
        navigate("/admin/welcome");
        return;
      }

      // Membre normal
      navigate("/membre/dashboard");
    } catch (error) {
      console.error("LOGIN ERROR:", error);

      toast.error(t("login.errors.network"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-800">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="
          w-full max-w-4xl 
          bg-white/95 rounded-3xl shadow-2xl 
          overflow-hidden grid md:grid-cols-2
        "
      >
        <div className="hidden md:flex flex-col items-center justify-center bg-gradient-to-br from-indigo-600 to-violet-700 text-white p-8 relative">
          <div className="flex flex-col items-center mb-6">
            <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg border border-white/30 mb-3">
              <img
                src="/images/aleoa.jpg"
                alt="Logo association"
                className="w-full h-full object-cover"
              />
            </div>
            <h2 className="text-xl font-extrabold tracking-wide text-center">
              {t("login.brand")}
            </h2>
          </div>

          <p className="text-sm text-white/80 text-center max-w-xs">
            {t("login.sideDescription")}
          </p>

          <div className="absolute bottom-4 left-6 text-[11px] text-white/60">
            {t("login.secureSpace")}
          </div>
        </div>

        <div className="bg-white px-6 py-8 sm:px-10 sm:py-10 flex flex-col justify-center">
          <div className="md:hidden flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-md border border-slate-200 mb-2">
              <img
                src="/images/aleoa.jpg"
                alt="Logo association"
                className="w-full h-full object-cover"
              />
            </div>
            <h2 className="text-lg font-bold text-slate-800 text-center">
              {t("login.mobileTitle")}
            </h2>
          </div>

          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 text-center mb-1">
            {t("login.title")}
          </h1>
          <p className="text-sm text-slate-500 text-center mb-6">
            {t("login.description")}
          </p>

          <form onSubmit={handleLogin} className="space-y-5" method="POST">
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                {t("login.memberId")}
              </label>
              {isSuspended && (
                <div className="mb-4 rounded-xl border border-orange-300 bg-orange-50 px-4 py-3 text-orange-800 flex items-center gap-2">
                  <AlertTriangle size={20} />
                  <span className="font-medium">{t("login.suspended")}</span>
                </div>
              )}
              <input
                className="w-full border border-slate-200 px-4 py-2.5 rounded-xl bg-slate-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
                placeholder={t("login.memberIdPlaceholder")}
                autoComplete="off"
                required
              />
            </div>

            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                {t("login.password")}
              </label>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full border border-slate-200 px-4 py-2.5 rounded-xl bg-slate-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none pr-11 text-sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  required
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  style={{ touchAction: "manipulation" }}
                >
                  {showPassword ? (
                    <FaEyeSlash size={18} />
                  ) : (
                    <FaEye size={18} />
                  )}
                </button>
              </div>
            </div>

            <motion.button
              whileTap={{ scale: 0.97 }}
              disabled={loading}
              className={`
                w-full py-2.5 rounded-xl text-white font-semibold text-sm shadow-md 
                flex items-center justify-center gap-2
                transition 
                ${
                  loading
                    ? "bg-indigo-300 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }
              `}
            >
              {loading ? t("login.loading") : t("login.submit")}
            </motion.button>
          </form>

          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
            <button
              type="button"
              onClick={() => (window.location.href = "/membre/forgot")}
              className="text-indigo-600 hover:text-indigo-700 hover:underline text-left"
            >
              {t("login.forgotPassword")}
            </button>

            <button
              type="button"
              onClick={() => (window.location.href = "/")}
              className="text-slate-500 hover:text-slate-700 hover:underline text-left sm:text-right"
            >
              {t("login.backHome")}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
